import React, { useMemo } from "react";
import { Box } from "@mui/material";
import { ParlorDetailProps } from "../../../types";
import { ColorSystemProps } from "../../../constants";
import hwajangDetail from "../../../constants/hwajang_detail.json";
import { haversineKm, formatDistance } from "../ParlorList/shuffle";

type HwajangRecord = {
  id: number;
  companyname: string;
  address: string;
  latitude: number;
  longitude: number;
  sido: number | string;
};

interface NearbyCrematoriaProps {
  defaultParlor: ParlorDetailProps;
  mainColor: ColorSystemProps;
}

export function NearbyCrematoria({
  defaultParlor,
  mainColor,
}: NearbyCrematoriaProps) {
  const { latitude, longitude, companyname, address, sido } = defaultParlor;

  // 같은 sido 화장장만 필터 (portal-agnostic) — 다른 포털에서도 동일 컴포넌트 사용 가능
  const sameSidoCrematoria = useMemo(
    () =>
      (hwajangDetail as HwajangRecord[]).filter(
        (h) => String(h.sido) === String(sido),
      ),
    [sido],
  );

  if (!latitude || !longitude || sameSidoCrematoria.length === 0) {
    return null;
  }

  const sorted = sameSidoCrematoria
    .map((h) => ({
      ...h,
      distanceKm: haversineKm(latitude, longitude, h.latitude, h.longitude),
    }))
    .sort((a, b) => a.distanceKm - b.distanceKm)
    .slice(0, 3);

  const sigungu = address.split(" ").slice(0, 2).join(" ");

  return (
    <Box>
      <Box
        style={{
          background: "#fafafa",
          border: "1px solid #edeae9",
          borderRadius: "16px",
          padding: "36px 24px",
        }}
      >
        <Box
          component="h2"
          style={{
            fontSize: "24px",
            fontWeight: 700,
            color: "#252525",
            lineHeight: 1.4,
          }}
        >
          {companyname}
          <br />
          인근 화장장
        </Box>
        <Box
          component="p"
          style={{
            marginTop: "14px",
            fontSize: "16px",
            lineHeight: 1.75,
            color: "#535353",
            maxWidth: "720px",
          }}
        >
          {sigungu} 기준 가까운 화장장 3곳입니다. 장례 마지막 단계인
          화장장 이동을 미리 계획하실 수 있도록 거리·주소를 함께
          안내합니다.
        </Box>
        <Box
          component="table"
          style={{
            width: "100%",
            marginTop: "24px",
            borderCollapse: "collapse",
            fontSize: "15px",
            lineHeight: 1.6,
          }}
        >
          <Box component="thead">
            <Box
              component="tr"
              style={{ borderBottom: "1px solid #e0e0e0" }}
            >
              <Box
                component="th"
                style={{
                  textAlign: "left",
                  padding: "10px 8px",
                  fontWeight: 600,
                  color: "#252525",
                }}
              >
                화장장
              </Box>
              <Box
                component="th"
                style={{
                  textAlign: "right",
                  padding: "10px 8px",
                  fontWeight: 600,
                  color: mainColor?.c500 || "#252525",
                  whiteSpace: "nowrap",
                }}
              >
                거리
              </Box>
              <Box
                component="th"
                style={{
                  textAlign: "left",
                  padding: "10px 8px",
                  fontWeight: 600,
                  color: "#252525",
                }}
              >
                주소
              </Box>
            </Box>
          </Box>
          <Box component="tbody">
            {sorted.map((h) => (
              <Box
                component="tr"
                key={h.id}
                style={{ borderBottom: "1px solid #f0f0f0" }}
              >
                <Box
                  component="td"
                  style={{
                    padding: "12px 8px",
                    color: "#252525",
                    fontWeight: 500,
                  }}
                >
                  {h.companyname}
                </Box>
                <Box
                  component="td"
                  style={{
                    padding: "12px 8px",
                    textAlign: "right",
                    color: mainColor?.c500 || "#252525",
                    fontWeight: 600,
                    whiteSpace: "nowrap",
                  }}
                >
                  {formatDistance(h.distanceKm)}
                </Box>
                <Box
                  component="td"
                  style={{
                    padding: "12px 8px",
                    color: "#535353",
                  }}
                >
                  {h.address}
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
        <Box
          component="p"
          style={{
            marginTop: "16px",
            fontSize: "13px",
            color: "#888",
          }}
        >
          ※ 직선거리 기준 산정값입니다. 실제 이동시간은 도로 사정에 따라
          달라질 수 있습니다.
        </Box>
      </Box>
    </Box>
  );
}
